import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import DashLayout from "@/components/DashLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Mail, Phone, Trash2, User, Calendar, Search, MessageSquare } from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLES = {
  new: "bg-blue-100 text-blue-800 border-blue-200",
  contacted: "bg-amber-100 text-amber-800 border-amber-200",
  admitted: "bg-emerald-100 text-emerald-800 border-emerald-200",
  rejected: "bg-slate-100 text-slate-700 border-slate-200",
};

export default function Enquiries() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/enquiries");
      setItems(data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    try {
      await api.put(`/enquiries/${id}`, { status });
      toast.success("Status updated");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this enquiry?")) return;
    try {
      await api.delete(`/enquiries/${id}`);
      toast.success("Deleted");
      if (selected?.id === id) setSelected(null);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const saveNotes = async () => {
    if (!selected) return;
    try {
      await api.put(`/enquiries/${selected.id}`, { notes });
      toast.success("Notes saved");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const openDetail = (e) => { setSelected(e); setNotes(e.notes || ""); };

  const filtered = items.filter((e) => {
    if (statusFilter !== "all" && e.status !== statusFilter) return false;
    if (q) {
      const hay = `${e.parent_name} ${e.parent_email} ${e.parent_phone} ${e.student_name || ""} ${e.student_class || ""}`.toLowerCase();
      if (!hay.includes(q.toLowerCase())) return false;
    }
    return true;
  });

  const counts = {
    total: items.length,
    new: items.filter((e) => e.status === "new").length,
    contacted: items.filter((e) => e.status === "contacted").length,
    admitted: items.filter((e) => e.status === "admitted").length,
  };

  return (
    <DashLayout variant="school" title="Admission Enquiries">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card className="p-4">
          <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Total</div>
          <div className="text-3xl font-black text-slate-900 mt-1" data-testid="enq-count-total">{counts.total}</div>
        </Card>
        <Card className="p-4">
          <div className="text-xs uppercase tracking-widest text-blue-600 font-bold">New</div>
          <div className="text-3xl font-black text-slate-900 mt-1" data-testid="enq-count-new">{counts.new}</div>
        </Card>
        <Card className="p-4">
          <div className="text-xs uppercase tracking-widest text-amber-600 font-bold">Contacted</div>
          <div className="text-3xl font-black text-slate-900 mt-1" data-testid="enq-count-contacted">{counts.contacted}</div>
        </Card>
        <Card className="p-4">
          <div className="text-xs uppercase tracking-widest text-emerald-600 font-bold">Admitted</div>
          <div className="text-3xl font-black text-slate-900 mt-1" data-testid="enq-count-admitted">{counts.admitted}</div>
        </Card>
      </div>

      <Card className="p-4 flex flex-wrap items-end gap-3 mb-4">
        <div className="flex-1 min-w-[220px]">
          <Label className="mb-1 block text-xs">Search</Label>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input className="pl-9" placeholder="Name, email, phone, class…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="enq-search" />
          </div>
        </div>
        <div>
          <Label className="mb-1 block text-xs">Status</Label>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40" data-testid="enq-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="new">New</SelectItem>
              <SelectItem value="contacted">Contacted</SelectItem>
              <SelectItem value="admitted">Admitted</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      <div className="bg-white rounded-lg border border-slate-200 divide-y">
        {filtered.length === 0 && (
          <div className="p-12 text-center text-slate-500" data-testid="enq-empty">
            <MessageSquare className="w-12 h-12 mx-auto text-slate-300 mb-3" />
            {items.length === 0 ? "No admission enquiries yet. When parents fill the 'Enquire Now' form on your school's public page, they'll appear here." : "No enquiries match the current filter."}
          </div>
        )}
        {filtered.map((e) => (
          <div key={e.id} className="p-4 hover:bg-slate-50 cursor-pointer" onClick={() => openDetail(e)} data-testid={`enq-row-${e.id}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="flex items-center gap-2 font-semibold text-slate-900">
                    <User className="w-4 h-4 text-slate-400" />{e.parent_name}
                  </div>
                  <Badge variant="outline" className={STATUS_STYLES[e.status] || ""} data-testid={`enq-status-${e.id}`}>{e.status}</Badge>
                  {e.student_class && <Badge variant="outline" className="border-slate-200 text-slate-600">Class {e.student_class}</Badge>}
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-x-6 gap-y-1 text-sm text-slate-600">
                  <span className="flex items-center gap-1"><Mail className="w-3.5 h-3.5 text-slate-400" />{e.parent_email}</span>
                  <span className="flex items-center gap-1"><Phone className="w-3.5 h-3.5 text-slate-400" />{e.parent_phone}</span>
                  {e.student_name && <span className="text-slate-500">Student: <b className="text-slate-800">{e.student_name}</b></span>}
                  <span className="flex items-center gap-1 text-xs text-slate-400"><Calendar className="w-3 h-3" />{new Date(e.created_at).toLocaleString()}</span>
                </div>
                {e.message && <div className="mt-2 text-sm text-slate-600 italic line-clamp-2">"{e.message}"</div>}
              </div>
              <div className="flex items-center gap-1 shrink-0" onClick={(ev) => ev.stopPropagation()}>
                <Select value={e.status} onValueChange={(v) => updateStatus(e.id, v)}>
                  <SelectTrigger className="w-32 h-8 text-xs" data-testid={`enq-status-select-${e.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="contacted">Contacted</SelectItem>
                    <SelectItem value="admitted">Admitted</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
                <Button size="sm" variant="ghost" onClick={() => remove(e.id)} className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0" data-testid={`enq-del-${e.id}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!selected} onOpenChange={(v) => { if (!v) setSelected(null); }}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Enquiry Details</DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div><div className="text-xs text-slate-500 uppercase tracking-widest">Parent</div><div className="font-semibold text-slate-900 mt-1">{selected.parent_name}</div></div>
                <div><div className="text-xs text-slate-500 uppercase tracking-widest">Status</div><Badge variant="outline" className={STATUS_STYLES[selected.status] || ""}>{selected.status}</Badge></div>
                <div><div className="text-xs text-slate-500 uppercase tracking-widest">Email</div><a href={`mailto:${selected.parent_email}`} className="text-blue-700 hover:underline block mt-1">{selected.parent_email}</a></div>
                <div><div className="text-xs text-slate-500 uppercase tracking-widest">Phone</div><a href={`tel:${selected.parent_phone}`} className="text-blue-700 hover:underline block mt-1">{selected.parent_phone}</a></div>
                <div><div className="text-xs text-slate-500 uppercase tracking-widest">Student</div><div className="mt-1">{selected.student_name || "—"}</div></div>
                <div><div className="text-xs text-slate-500 uppercase tracking-widest">Applying for Class</div><div className="mt-1">{selected.student_class || "—"}</div></div>
              </div>
              {selected.message && (
                <div>
                  <div className="text-xs text-slate-500 uppercase tracking-widest">Message from parent</div>
                  <div className="mt-1 bg-slate-50 border border-slate-200 rounded p-3 text-slate-700 whitespace-pre-wrap">{selected.message}</div>
                </div>
              )}
              <div>
                <Label className="mb-1 block text-xs">Internal notes</Label>
                <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="enq-notes" />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setSelected(null)}>Close</Button>
                <Button className="bg-blue-700 hover:bg-blue-800" onClick={saveNotes} data-testid="enq-save-notes">Save Notes</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DashLayout>
  );
}
